import React from "react";
import { Heart, Building2, MapPin, DollarSign, Sparkles } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";

// Source of truth for the number of questions per psychographic wave
// Must stay in sync with /app/frontend/src/components/PropertyQuestionnaire.js
export const WAVE_TOTALS = {
  emotional: 15,
  technical: 12,
  urban: 10,
  financial: 15,
};

export const TOTAL_QUESTIONS = Object.values(WAVE_TOTALS).reduce((a, b) => a + b, 0); // 52

const WAVE_META = {
  emotional: { label: "ADN Emocional", short: "Emocional", icon: Heart, color: "text-pink-600", bg: "bg-pink-50", border: "border-pink-200" },
  technical: { label: "Arquitectura", short: "Arquitectura", icon: Building2, color: "text-blue-600", bg: "bg-blue-50", border: "border-blue-200" },
  urban: { label: "Entorno y Urbanismo", short: "Entorno", icon: MapPin, color: "text-emerald-600", bg: "bg-emerald-50", border: "border-emerald-200" },
  financial: { label: "Viabilidad Financiera", short: "Viabilidad", icon: DollarSign, color: "text-amber-600", bg: "bg-amber-50", border: "border-amber-200" },
};

// Count how many questions are answered per wave from a psychographic_attributes object.
// Skips null/undefined/"skipped" values so those don't count as answered.
export const countAnswersPerWave = (attrs = {}) => {
  const result = { emotional: 0, technical: 0, urban: 0, financial: 0 };
  ["emotional", "technical", "urban", "financial"].forEach((cat) => {
    const bucket = attrs[cat] || {};
    Object.values(bucket).forEach((v) => {
      if (v !== null && v !== undefined && v !== "skipped") result[cat] += 1;
    });
  });
  return result;
};

export const getCompletionSummary = (attrs = {}) => {
  const perWave = countAnswersPerWave(attrs);
  const answered = Object.values(perWave).reduce((a, b) => a + b, 0);
  return {
    perWave,
    answered,
    total: TOTAL_QUESTIONS,
    percent: Math.round((answered / TOTAL_QUESTIONS) * 100),
  };
};

// Compact inline badge (for property cards in lists)
export const PsychographicBadge = ({ psychographicAttributes, onClick, className = "" }) => {
  const { answered, total, percent } = getCompletionSummary(psychographicAttributes);

  const tone =
    percent >= 80 ? "bg-green-100 text-green-800 border-green-200"
    : percent >= 50 ? "bg-amber-100 text-amber-800 border-amber-200"
    : "bg-red-100 text-red-800 border-red-200";

  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        if (onClick) onClick();
      }}
      className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md border text-xs font-medium transition-colors hover:opacity-90 ${tone} ${className}`}
      title={`Perfil psicográfico ${answered}/${total} (${percent}%)`}
      data-testid="psychographic-badge"
    >
      <Sparkles className="w-3 h-3" />
      <span>Perfil {answered}/{total}</span>
      <span className="opacity-70">• {percent}%</span>
    </button>
  );
};

// Full gauge card (for PropertyDetailModal)
export const PsychographicGauge = ({ psychographicAttributes, onEditWave, className = "" }) => {
  const { perWave, answered, total, percent } = getCompletionSummary(psychographicAttributes);

  return (
    <Card className={`border-2 ${percent >= 80 ? "border-green-200" : percent >= 50 ? "border-amber-200" : "border-red-200"} ${className}`} data-testid="psychographic-gauge">
      <CardContent className="pt-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-primary" />
            <h4 className="font-semibold text-slate-800">Completitud del Perfil Psicográfico</h4>
          </div>
          <Badge variant={percent >= 80 ? "default" : "secondary"} className="text-xs">
            {answered}/{total} • {percent}%
          </Badge>
        </div>
        <Progress value={percent} className="h-2" />
        <p className="text-xs text-slate-500">
          {percent === 100
            ? "Perfil completo. Matching psicográfico al máximo."
            : percent >= 80
            ? "Muy completo. Pocos ajustes para matching óptimo."
            : percent >= 50
            ? "Buen avance. Completa los factores faltantes para mejorar el matching."
            : "Perfil básico. Complétalo para desbloquear todo el potencial del matching."}
        </p>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 pt-2">
          {["emotional", "technical", "urban", "financial"].map((cat) => {
            const meta = WAVE_META[cat];
            const Icon = meta.icon;
            const count = perWave[cat];
            const totalCat = WAVE_TOTALS[cat];
            const isComplete = count >= totalCat;
            return (
              <button
                key={cat}
                type="button"
                onClick={() => onEditWave && onEditWave(cat)}
                className={`text-left p-2 rounded-lg border transition-all hover:shadow-sm ${meta.bg} ${meta.border} ${onEditWave ? "cursor-pointer" : "cursor-default"}`}
                data-testid={`wave-stat-${cat}`}
                disabled={!onEditWave}
              >
                <div className="flex items-center gap-2 mb-1">
                  <Icon className={`w-4 h-4 ${meta.color}`} />
                  <span className="text-xs font-semibold text-slate-700">{meta.short}</span>
                </div>
                <div className="flex items-baseline gap-1">
                  <span className={`text-lg font-bold ${isComplete ? "text-green-700" : meta.color}`}>{count}</span>
                  <span className="text-xs text-slate-500">/ {totalCat}</span>
                  {isComplete && <span className="ml-auto text-green-600 text-xs">✓</span>}
                </div>
              </button>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};
